import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Active,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  type Over,
} from "@dnd-kit/core";
import { tasksApi, ApiError } from "@/api";
import type { BoardStage, TaskBoardRow } from "@/types";
import { Pill, Toast, useToast } from "@/components/ui";
import { taskPriorityTone, deadlineChip, humanise, taskTypeLabel } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { celebrate } from "@/lib/confetti";
import { useAuth } from "@/staff/auth/AuthContext";
import { useStaffUsers } from "./OwnerSelect";
import "./task-board.css";

const BOARD_COLUMNS: { stage: BoardStage; label: string }[] = [
  { stage: "in_progress", label: "In progress" },
  { stage: "up_next", label: "Up next" },
  { stage: "later", label: "Later" },
  { stage: "with_client", label: "With client" },
  { stage: "completed", label: "Completed" },
  { stage: "not_scheduled", label: "Not scheduled" },
];
const STAGES = new Set<BoardStage>(BOARD_COLUMNS.map((c) => c.stage));
const PRIORITY_RANK: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };

// Dropping into Completed is deferred this long so an accidental drop can be
// undone (drag the card back out, or hit Undo) before the PATCH fires.
const COMPLETE_DELAY_SECS = 5;

// The lane's card gap (--space-3) — cards below the landing slot shift down by
// the dragged card's height plus this, opening a true-size gap.
const LANE_GAP_PX = 12;

type Buckets = Record<BoardStage, TaskBoardRow[]>;
type Origin = { board_stage: BoardStage; status: string; completed_at: number | null };
/** Where a drag currently points: the lane, plus (when over a card) which card
 *  and which of its edges the dragged card would land at. cardId null = the
 *  lane's empty space → append at the end. */
type DropSpot = { stage: BoardStage; cardId: number | null; edge: "before" | "after" };
/** A card's resolved assignee: roster name + optional avatar image URL. */
type Assignee = { name: string; avatarUrl: string | null };

/** Fallback ordering (brief §6) for cards never hand-placed: priority
 *  urgent→low, then due date ascending (unset last), then created_at. */
function compareCards(a: TaskBoardRow, b: TaskBoardRow): number {
  const pr = (PRIORITY_RANK[b.priority] ?? 0) - (PRIORITY_RANK[a.priority] ?? 0);
  if (pr !== 0) return pr;
  const ad = a.due_date && a.due_date > 0 ? a.due_date : Number.POSITIVE_INFINITY;
  const bd = b.due_date && b.due_date > 0 ? b.due_date : Number.POSITIVE_INFINITY;
  if (ad !== bd) return ad - bd;
  return a.created_at - b.created_at;
}

/** Display order within a lane: hand-placed cards first by board_position
 *  (1-based, written by PATCH /reorder), then never-placed ones (position 0 —
 *  e.g. tasks created after the lane was last hand-ordered) by compareCards. */
function compareBoardOrder(a: TaskBoardRow, b: TaskBoardRow): number {
  const ap = a.board_position > 0 ? a.board_position : Number.POSITIVE_INFINITY;
  const bp = b.board_position > 0 ? b.board_position : Number.POSITIVE_INFINITY;
  if (ap !== bp) return ap - bp;
  return compareCards(a, b);
}

/** Collision detection: lane first, then a card within it. The lane is the one
 *  the dragged card's centre is over horizontally (the lanes are one row, so x
 *  alone decides; the nearest lane if the centre is in a gutter or over a lane
 *  header). Only that lane's cards then compete, via closestCorners, for the
 *  landing spot; a lane with no cards returns the lane itself (append).
 *  Plain closestCorners over every droppable made an EMPTY lane undroppable: its
 *  drop zone is lane-tall, so its far corners pushed its average corner distance
 *  past that of a card one column over, and the drop landed in the neighbour. */
const laneThenCard: CollisionDetection = (args) => {
  const { collisionRect, droppableRects, droppableContainers } = args;
  const x = collisionRect.left + collisionRect.width / 2;
  let lane: BoardStage | null = null;
  let nearest = Number.POSITIVE_INFINITY;
  for (const c of droppableContainers) {
    if (!STAGES.has(c.id as BoardStage)) continue;
    const rect = droppableRects.get(c.id);
    if (!rect) continue;
    const d = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
    if (d < nearest) { nearest = d; lane = c.id as BoardStage; }
  }
  if (lane == null) return [];
  const cards = droppableContainers.filter((c) => c.data.current?.stage === lane);
  return cards.length > 0 ? closestCorners({ ...args, droppableContainers: cards }) : [{ id: lane }];
};

function initials(name: string): string {
  const parts = name.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  const chars = (parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "");
  return chars.toUpperCase() || name.slice(0, 1).toUpperCase();
}

/** The card's assignee chip: their photo when the roster carries one, else
 *  initials. A URL that fails to load falls back to initials for good rather
 *  than showing the browser's broken-image glyph. */
function AssigneeAvatar({ assignee }: { assignee: Assignee }) {
  const [broken, setBroken] = useState(false);
  const url = broken ? null : assignee.avatarUrl;
  return (
    <span className="tboard-card__avatar" title={`Assigned to ${assignee.name}`}>
      {url ? <img src={url} alt="" onError={() => setBroken(true)} /> : initials(assignee.name)}
    </span>
  );
}

/** Viewport-normalised confetti origin for a lane: the top of its drop zone,
 *  read live from the DOM so it's correct even if the board scrolled during the
 *  undo window. `undefined` if the lane isn't currently mounted → centre fallback. */
function laneBurstOrigin(stage: BoardStage): { x: number; y: number } | undefined {
  if (typeof document === "undefined") return undefined;
  const el = document.querySelector(`.tboard-col__drop[data-stage="${stage}"]`);
  if (!el) return undefined;
  const rect = el.getBoundingClientRect();
  // +48px ≈ the first card slot — where the completing card sits and vanishes from.
  return { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + 48) / window.innerHeight };
}

/**
 * Tasks Kanban — additive to the list view. Self-fetches the working set from
 * GET /board, buckets by `board_stage`, and persists column moves via a single
 * PATCH /move (optimistic, with rollback + toast on failure). Cards can also be
 * hand-ordered within a lane: every card is a drop target, a drop lands the
 * card at the hovered card's nearer edge — cards at/below that slot shift down
 * to preview the landing spot (a placeholder marks an end-of-lane slot), while
 * the dragged card's own slot closes behind it — and the lane's FULL new order
 * is persisted via PATCH /reorder (board_position;
 * the Mine filter is display-only so hidden cards keep their slots). Optionally
 * scoped to a job/application; `reloadToken` forces a refetch after an external
 * edit.
 */
export function TaskBoard({
  jobId,
  applicationId,
  reloadToken,
  fill = false,
  onOpenTask,
  onMoved,
}: {
  jobId?: number;
  applicationId?: number;
  /** Bump to force a refetch (e.g. after the task drawer saves). */
  reloadToken?: number;
  /** Stretch the lanes to fill the viewport height (standalone board page). */
  fill?: boolean;
  /** Card click — open the existing task editor. No-op if omitted. */
  onOpenTask?: (row: TaskBoardRow) => void;
  /** Fired after a successful move with the reconciled row (e.g. so a parent
   *  can re-fetch its own list/detail to stay in sync). */
  onMoved?: (row: TaskBoardRow) => void;
}) {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const currentUserName = user?.name || user?.email;
  // Staff roster (api 343) so any assignee's card can carry their avatar, not
  // just the signed-in user's. Loads once; on failure the board degrades to
  // naming only the current user's own cards.
  const { staff } = useStaffUsers();
  const staffById = useMemo(() => new Map((staff ?? []).map((u) => [u.id, u])), [staff]);

  const [rows, setRows] = useState<TaskBoardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [mine, setMine] = useState(false);
  const [movingIds, setMovingIds] = useState<Set<number>>(() => new Set());
  const [pendingIds, setPendingIds] = useState<Set<number>>(() => new Set());
  const [activeId, setActiveId] = useState<number | null>(null);
  // Live drop target while dragging — drives the lane highlight and the
  // gap that opens at the landing slot.
  const [dragOver, setDragOver] = useState<DropSpot | null>(null);
  // The dragged card's measured height — the size of the gap the lane opens.
  const [activeSize, setActiveSize] = useState<number | null>(null);
  const { toast, showToast } = useToast();
  // The deferred-completion toast (latest pending card), with a live countdown.
  const [undo, setUndo] = useState<{ id: number; title: string; secondsLeft: number } | null>(null);

  // A real drag fired between pointerdown/up — suppress the click that follows
  // so a drag doesn't also open the card. Pure clicks never set this.
  const didDragRef = useRef(false);
  // Per-card commit timers + their pre-drop origin (to commit or revert).
  const completeTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const completeOrigins = useRef<Map<number, Origin>>(new Map());
  const undoTick = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (undoTick.current) clearInterval(undoTick.current);
    for (const t of completeTimers.current.values()) clearTimeout(t);
  }, []);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    tasksApi
      .getTaskBoard({ job_id: jobId, application_id: applicationId })
      // The Completed lane is a "drop to mark done" target, not an archive — we
      // don't load already-completed tasks (they'd pile up over the backend's
      // 14-day window). A card flashes into Completed on drop, then disappears
      // once its PATCH lands (see move()). History lives in the List view.
      // On the GLOBAL board we also drop tasks whose parent job is on hold — a
      // paused job's work shouldn't clutter the cross-job working set. When the
      // board is scoped to a single job/application the user has navigated there
      // deliberately, so those stay visible (the List view shows them too).
      .then((items) => {
        if (cancelled) return;
        const scoped = jobId != null || applicationId != null;
        setRows(items.filter((r) => r.board_stage !== "completed" && (scoped || r.job_status !== "on_hold")));
      })
      .catch((err) => { if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the board."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId, applicationId]);

  useEffect(() => load(), [load, reloadToken]);

  const buckets = useMemo<Buckets>(() => {
    const map: Buckets = { in_progress: [], up_next: [], later: [], with_client: [], completed: [], not_scheduled: [] };
    for (const r of rows) {
      if (mine && currentUserId != null && r.assigned_to_user_id !== currentUserId) continue;
      if (map[r.board_stage]) map[r.board_stage].push(r);
    }
    for (const stage of STAGES) map[stage].sort(compareBoardOrder);
    return map;
  }, [rows, mine, currentUserId]);

  const visibleCount = useMemo(
    () => BOARD_COLUMNS.reduce((n, c) => n + buckets[c.stage].length, 0),
    [buckets],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  function handleDragStart(event: DragStartEvent) {
    didDragRef.current = true;
    setActiveId(Number(event.active.id));
    setActiveSize(event.active.rect.current.initial?.height ?? null);
  }

  // Let the trailing click see didDragRef=true (so a drag doesn't also open the
  // card), then clear it on the next tick.
  function endDrag() {
    setActiveId(null);
    setDragOver(null);
    setActiveSize(null);
    setTimeout(() => { didDragRef.current = false; }, 0);
  }

  /** Where does this drag currently point? Over a lane with no cards → that
   *  lane, append (laneThenCard only reports a lane then). Over a card → that
   *  card's lane, landing at whichever of its edges is nearer the dragged card's
   *  centre. */
  function resolveSpot(active: Active, over: Over): DropSpot | null {
    const overId = String(over.id);
    if (STAGES.has(overId as BoardStage)) return { stage: overId as BoardStage, cardId: null, edge: "after" };
    if (!overId.startsWith("card-")) return null;
    const anchor = rows.find((r) => r.id === Number(overId.slice(5)));
    if (!anchor) return null;
    const rect = active.rect.current.translated;
    const edge =
      rect && rect.top + rect.height / 2 > over.rect.top + over.rect.height / 2 ? "after" : "before";
    return { stage: anchor.board_stage, cardId: anchor.id, edge };
  }

  function handleDragMove(event: DragMoveEvent) {
    if (activeSize == null) setActiveSize(event.active.rect.current.initial?.height ?? null);
    const next = event.over ? resolveSpot(event.active, event.over) : null;
    setDragOver((cur) =>
      cur?.stage === next?.stage && cur?.cardId === next?.cardId && cur?.edge === next?.edge ? cur : next,
    );
  }

  /** Visible index in `cards` where the gap opens (the dragged card's landing
   *  slot), or null for no gap in this lane. `cards.length` = an append slot,
   *  rendered as a placeholder since there's nothing below to shift. Completed
   *  gets no preview — its cards leave the board rather than staying put. */
  function laneGapIndex(stage: BoardStage, cards: TaskBoardRow[]): number | null {
    if (!dragOver || dragOver.stage !== stage || activeSize == null) return null;
    if (stage === "completed") return null;
    if (dragOver.cardId === activeId) {
      // Hovering its own slot (a no-op drop) → the gap stays at the origin.
      const i = cards.findIndex((c) => c.id === activeId);
      return i < 0 ? null : i;
    }
    if (dragOver.cardId == null) return cards.length;
    const i = cards.findIndex((c) => c.id === dragOver.cardId);
    if (i < 0) return cards.length;
    return dragOver.edge === "after" ? i + 1 : i;
  }

  /** The lane's full order (Mine filter ignored — it's display-only). */
  function laneOrder(stage: BoardStage): TaskBoardRow[] {
    return rows.filter((r) => r.board_stage === stage).sort(compareBoardOrder);
  }

  /** The target lane's full id order with `card` placed at `spot`; null when the
   *  drop would change nothing (already sitting there, positions already dense). */
  function orderedIdsAfterDrop(card: TaskBoardRow, spot: DropSpot): number[] | null {
    const lane = laneOrder(spot.stage).filter((r) => r.id !== card.id);
    let at = lane.length;
    if (spot.cardId != null) {
      const i = lane.findIndex((r) => r.id === spot.cardId);
      if (i >= 0) at = spot.edge === "before" ? i : i + 1;
    }
    const ids = lane.map((r) => r.id);
    ids.splice(at, 0, card.id);
    if (card.board_stage === spot.stage) {
      const current = laneOrder(spot.stage);
      if (current.every((r, i) => r.id === ids[i] && r.board_position === i + 1)) return null;
    }
    return ids;
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const spot = over ? resolveSpot(active, over) : null;
    endDrag();
    if (!spot) return;
    const id = Number(active.id);
    const card = rows.find((r) => r.id === id);
    if (!card || spot.cardId === id) return; // dropped on itself → no-op

    // Submit cards can be dragged between scheduling lanes, but Completed isn't a
    // manual target — they close automatically when the application is logged. A
    // drop on Completed bounces back to its lane (no PATCH, no optimistic move)
    // with a hint, rather than completing. A draw card drives its drawing's status
    // across every lane (no client-side restriction). Ordering inside Completed
    // is meaningless (cards leave the board on commit), so it's never persisted.
    if (spot.stage === "completed") {
      if (card.board_stage === "completed") return;
      if (card.task_type === "submit") {
        showToast("Submit tasks close automatically when the application is logged");
        return;
      }
      void move(card, "completed");
      return;
    }

    const orderedIds = orderedIdsAfterDrop(card, spot);
    if (!orderedIds) return;
    if (card.board_stage === spot.stage) void reorderWithin(card, orderedIds);
    else void move(card, spot.stage, orderedIds);
  }

  /** Optimistically stamp a lane's new 1-based positions onto rows; returns the
   *  overwritten positions keyed by id, for rollback. */
  function applyPositions(orderedIds: number[]): Map<number, number> {
    const pos = new Map(orderedIds.map((tid, i) => [tid, i + 1]));
    const prev = new Map<number, number>();
    for (const r of rows) {
      const p = pos.get(r.id);
      if (p != null && p !== r.board_position) prev.set(r.id, r.board_position);
    }
    setRows((rs) =>
      rs.map((r) => {
        const p = pos.get(r.id);
        return p == null || r.board_position === p ? r : { ...r, board_position: p };
      }),
    );
    return prev;
  }

  function restorePositions(prev: Map<number, number>) {
    setRows((rs) => rs.map((r) => (prev.has(r.id) ? { ...r, board_position: prev.get(r.id)! } : r)));
  }

  /** Same-lane drop → persist the lane's new order (optimistic, rollback + toast
   *  on failure). No /move — the stage didn't change. */
  async function reorderWithin(card: TaskBoardRow, orderedIds: number[]) {
    if (movingIds.has(card.id)) return;
    const prevPositions = applyPositions(orderedIds);
    setMovingIds((s) => new Set(s).add(card.id));
    try {
      await tasksApi.reorderBoard({ ordered_ids: orderedIds });
    } catch {
      restorePositions(prevPositions);
      showToast("Couldn't reorder — try again");
    } finally {
      setMovingIds((s) => { const next = new Set(s); next.delete(card.id); return next; });
    }
  }

  async function move(card: TaskBoardRow, target: BoardStage, orderedIds?: number[]) {
    if (movingIds.has(card.id)) return;

    // If the card is mid-undo-window, its true origin is the stored pre-drop one
    // (not the optimistic "completed"); cancel the deferred commit and reuse it.
    const stored = completeOrigins.current.get(card.id);
    if (pendingIds.has(card.id)) cancelComplete(card.id);
    const prev: Origin = stored ?? { board_stage: card.board_stage, status: card.status, completed_at: card.completed_at };

    // Optimistic: the stage, and (for a positioned drop) the target lane's order.
    const prevPositions = orderedIds ? applyPositions(orderedIds) : null;
    setRows((rs) => rs.map((r) => (r.id === card.id ? { ...r, board_stage: target } : r)));

    // Completed is deferred — let the user undo (drag back out / Undo) first.
    // The confetti is held back until the completion actually commits (after the
    // undo window elapses), bursting from the lane the card vanishes from — see
    // commitComplete.
    if (target === "completed") {
      scheduleComplete(card, prev);
      return;
    }

    setMovingIds((s) => new Set(s).add(card.id));
    try {
      const updated = await tasksApi.moveTaskStage({ id: card.id, board_stage: target });
      if (orderedIds) await tasksApi.reorderBoard({ ordered_ids: orderedIds });
      // Reconcile only the server-owned fields; keep our normalised job/* fields
      // (and our optimistic board_position — /move's response predates /reorder).
      setRows((rs) =>
        rs.map((r) =>
          r.id === card.id
            ? { ...r, board_stage: updated.board_stage, status: updated.status, completed_at: updated.completed_at }
            : r,
        ),
      );
      onMoved?.({ ...card, ...updated });
    } catch {
      setRows((rs) => rs.map((r) => (r.id === card.id ? { ...r, ...prev } : r))); // rollback
      if (prevPositions) restorePositions(prevPositions);
      showToast("Couldn't move task — try again");
    } finally {
      setMovingIds((s) => { const next = new Set(s); next.delete(card.id); return next; });
    }
  }

  // ── Deferred completion (undo window) ──────────────────────────────────────

  function stopTick() {
    if (undoTick.current) { clearInterval(undoTick.current); undoTick.current = null; }
  }
  /** Clear the undo toast + countdown, but only if it's showing this card. */
  function dismissUndoIf(id: number) {
    setUndo((u) => {
      if (u && u.id === id) { stopTick(); return null; }
      return u;
    });
  }

  /** Card sits optimistically in Completed; after the delay it commits (PATCH +
   *  drop off the board). The toast shows a live countdown for the latest card. */
  function scheduleComplete(card: TaskBoardRow, prev: Origin) {
    completeOrigins.current.set(card.id, prev);
    setPendingIds((s) => new Set(s).add(card.id));
    completeTimers.current.set(card.id, setTimeout(() => commitComplete(card.id), COMPLETE_DELAY_SECS * 1000));

    setUndo({ id: card.id, title: card.title, secondsLeft: COMPLETE_DELAY_SECS });
    stopTick();
    undoTick.current = setInterval(() => {
      setUndo((u) => {
        if (!u) { stopTick(); return u; }
        if (u.secondsLeft <= 1) { stopTick(); return { ...u, secondsLeft: 0 }; }
        return { ...u, secondsLeft: u.secondsLeft - 1 };
      });
    }, 1000);
  }

  /** Tear down a card's pending bookkeeping (no PATCH); returns its origin. */
  function cancelComplete(id: number): Origin | undefined {
    const prev = completeOrigins.current.get(id);
    const timer = completeTimers.current.get(id);
    if (timer) clearTimeout(timer);
    completeTimers.current.delete(id);
    completeOrigins.current.delete(id);
    setPendingIds((s) => { const next = new Set(s); next.delete(id); return next; });
    dismissUndoIf(id);
    return prev;
  }

  /** The delay elapsed → persist the completion and drop the card off the board. */
  async function commitComplete(id: number) {
    // Recompute the burst origin live — the lane may have scrolled during the undo
    // window. The card is still on the board here, so the Completed lane is mounted.
    const burst = laneBurstOrigin("completed");
    const prev = cancelComplete(id); // clears timers/pending/toast for this card
    setMovingIds((s) => new Set(s).add(id));
    try {
      const updated = await tasksApi.moveTaskStage({ id, board_stage: "completed" });
      setRows((rs) => rs.filter((r) => r.id !== id)); // done → off the board
      celebrate(burst); // undo window elapsed → celebrate as the card leaves the board
      onMoved?.(updated);
    } catch {
      if (prev) setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...prev } : r))); // rollback
      showToast("Couldn't complete task — try again");
    } finally {
      setMovingIds((s) => { const next = new Set(s); next.delete(id); return next; });
    }
  }

  /** Undo button — revert the card to its pre-drop lane, no PATCH. */
  function undoComplete() {
    if (!undo) return;
    const { id } = undo;
    const prev = cancelComplete(id);
    if (prev) setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...prev } : r)));
  }

  /** A card's assignee for display: the roster names (and pictures) anyone on
   *  staff; while it's loading (or failed) fall back to naming the current
   *  user's own cards, initials-only. */
  function assigneeFor(row: TaskBoardRow): Assignee | null {
    const id = row.assigned_to_user_id;
    if (id == null || id <= 0) return null;
    const u = staffById.get(id);
    if (u) return { name: u.name, avatarUrl: u.avatar_url ?? null };
    if (currentUserId != null && id === currentUserId) return { name: currentUserName || "You", avatarUrl: null };
    return null;
  }

  const activeCard = activeId != null ? rows.find((r) => r.id === activeId) ?? null : null;
  // tboard--dragging scopes the transform transition to live drags, so the real
  // reflow on drop snaps instead of double-animating (layout jump + transform decay).
  const rootClass = `tboard${fill ? " tboard--fill" : ""}${activeId != null ? " tboard--dragging" : ""}`;

  if (loading) {
    return (
      <div className={rootClass}>
        <div className="tboard__cols">
          {BOARD_COLUMNS.map((c) => (
            <div key={c.stage} className="tboard-col">
              <div className="tboard-col__head"><span className="tboard-col__label">{c.label}</span></div>
              <div className="tboard-col__drop">
                {Array.from({ length: 2 }).map((_, i) => <div key={i} className="tboard-card tboard-card--skel" aria-hidden="true" />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="leads__error" role="alert">
        {error}{" "}
        <button type="button" className="tdash__retry" onClick={load}>Retry</button>
      </p>
    );
  }

  return (
    <div className={rootClass}>
      {currentUserId != null ? (
        <div className="tboard__toolbar">
          <div className="tdash__pills" role="group" aria-label="Assignee filter">
            <button type="button" className={`tdash-pill${mine ? "" : " is-on"}`} onClick={() => setMine(false)}>All</button>
            <button type="button" className={`tdash-pill${mine ? " is-on" : ""}`} onClick={() => setMine(true)}>Mine</button>
          </div>
        </div>
      ) : null}

      {visibleCount === 0 ? (
        <p className="jobs-list__empty">
          {mine
            ? "Nothing assigned to you. Switch to All, or create a task."
            : "No tasks on the board. Switch to the list view or create a task."}
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={laneThenCard} onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd} onDragCancel={endDrag}>
          <div className="tboard__cols">
            {BOARD_COLUMNS.map((col) => {
              const cards = buckets[col.stage];
              const gapAt = laneGapIndex(col.stage, cards);
              // The dragged card is hidden but keeps its layout slot; as soon as
              // the drag points somewhere, cards below it close that slot (shift
              // up) while cards at/below the landing slot open the gap (shift
              // down) — the two cancel for cards below both, so a same-lane drag
              // reads as the card simply changing places.
              const activeIdx = dragOver != null && activeId != null ? cards.findIndex((c) => c.id === activeId) : -1;
              const step = activeSize != null ? activeSize + LANE_GAP_PX : 0;
              return (
                <BoardColumn
                  key={col.stage}
                  stage={col.stage}
                  label={col.label}
                  cards={cards}
                  isDropTarget={dragOver?.stage === col.stage}
                >
                  {cards.map((card, i) => {
                    let shift = 0;
                    if (activeIdx >= 0 && i > activeIdx) shift -= step;
                    if (gapAt != null && i >= gapAt) shift += step;
                    return (
                      <BoardCard
                        key={card.id}
                        row={card}
                        moving={movingIds.has(card.id)}
                        pending={pendingIds.has(card.id)}
                        shiftY={shift !== 0 ? shift : null}
                        assignee={assigneeFor(card)}
                        onOpen={(r) => { if (!didDragRef.current) onOpenTask?.(r); }}
                      />
                    );
                  })}
                  {/* Append slot placeholder — only for a lane the card ISN'T in:
                      in its own lane the vacated slot at the bottom is the gap. */}
                  {gapAt === cards.length && activeIdx < 0 && activeSize != null ? (
                    <div className="tboard-card--slot" style={{ height: activeSize }} aria-hidden="true" />
                  ) : null}
                </BoardColumn>
              );
            })}
          </div>

          <DragOverlay>
            {activeCard ? (
              <div className={`tboard-card tboard-card--overlay${activeCard.task_type ? ` tboard-card--${activeCard.task_type}` : ""}`}>
                <CardBody row={activeCard} assignee={assigneeFor(activeCard)} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {undo ? (
        <div className="tboard__undo" role="status">
          <span className="tboard__undo-text">
            Completing <strong>{undo.title}</strong> in {undo.secondsLeft}s
          </span>
          <button type="button" className="tboard__undo-btn" onClick={undoComplete}>Undo</button>
        </div>
      ) : null}

      <Toast toast={toast} />
    </div>
  );
}

function BoardColumn({
  stage,
  label,
  cards,
  isDropTarget,
  children,
}: {
  stage: BoardStage;
  label: string;
  cards: TaskBoardRow[];
  /** Parent-computed (from the live DropSpot) — a drag over any card in the lane
   *  should highlight the lane, not just a drag over the lane's empty space, so
   *  this droppable's own isOver isn't enough. */
  isDropTarget: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: stage });
  return (
    <section className="tboard-col">
      <div className="tboard-col__head">
        <span className="tboard-col__label">{label}</span>
        <span className="tboard-col__count numeric">{cards.length}</span>
      </div>
      <div ref={setNodeRef} data-stage={stage} className={`tboard-col__drop${isDropTarget ? " is-over" : ""}`}>
        {/* An empty lane being hovered renders children anyway — they hold the
            landing-slot placeholder that replaces "Nothing here". */}
        {cards.length === 0 && !isDropTarget ? <p className="tboard-col__empty">Nothing here</p> : children}
      </div>
    </section>
  );
}

function BoardCard({
  row,
  moving,
  pending,
  shiftY,
  assignee,
  onOpen,
}: {
  row: TaskBoardRow;
  moving: boolean;
  pending: boolean;
  /** Set while a drag is live: how far to slide (positive = down, opening the
   *  landing gap; negative = up, closing the dragged card's vacated slot).
   *  Purely visual — droppable rects were measured at drag start, so
   *  hit-testing stays on the unshifted layout. */
  shiftY: number | null;
  /** Resolved assignee (null = unassigned or unresolvable) — drives the
   *  bottom-right avatar: their photo when the roster has one, else initials. */
  assignee: Assignee | null;
  onOpen: (row: TaskBoardRow) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(row.id),
    disabled: moving,
  });
  // Each card is also a drop target so a drag can land BETWEEN cards (manual
  // ordering), not just on a lane. Same DOM node carries both refs. `stage`
  // lets laneThenCard keep only the chosen lane's cards in the running.
  const { setNodeRef: setDropRef } = useDroppable({ id: `card-${row.id}`, data: { stage: row.board_stage } });
  // The dragged original never moves — the DragOverlay tracks the pointer; the
  // original is hidden (CSS) and its slot closed by the other cards' shifts.
  const style = shiftY != null && !isDragging ? { transform: `translateY(${shiftY}px)` } : undefined;
  return (
    <div
      ref={(node) => { setNodeRef(node); setDropRef(node); }}
      style={style}
      className={`tboard-card${row.task_type ? ` tboard-card--${row.task_type}` : ""}${isDragging ? " is-dragging" : ""}${moving ? " is-moving" : ""}${pending ? " is-pending" : ""}`}
      onClick={() => onOpen(row)}
      {...listeners}
      {...attributes}
    >
      <CardBody row={row} assignee={assignee} />
      {moving ? <span className="tboard-card__spinner" aria-label="Moving…" /> : null}
    </div>
  );
}

function CardBody({
  row,
  assignee,
}: {
  row: TaskBoardRow;
  assignee: Assignee | null;
}) {
  const hasDate = row.due_date != null && row.due_date > 0;
  const overdue = row.days_remaining != null && row.days_remaining < 0;
  // The job (name only — number dropped) is the card's headline; the task title
  // sits below it in the meta row. A task with no job falls back to its title as
  // the headline so the card is never headless (and isn't repeated below).
  const headline = row.job_name || row.title;
  const subTitle = row.job_name ? row.title : null;

  return (
    <>
      <div className="tboard-card__top">
        <span className="tboard-card__title" title={headline}>{headline}</span>
        {row.status === "blocked" ? <span className="tboard-card__blocked">Blocked</span> : null}
      </div>

      {row.task_type === "draw" || row.task_type === "submit" ? (
        <span className={`tboard-card__type tboard-card__type--${row.task_type}`}>{taskTypeLabel(row.task_type)}</span>
      ) : null}

      <div className="tboard-card__pills">
        <Pill tone={taskPriorityTone(row.priority)}>{humanise(row.priority)}</Pill>
        {hasDate ? (
          <span className={`tboard-card__due${overdue ? " is-overdue" : ""}`} title={deadlineChip(row.days_remaining).label}>
            {formatDateShort(row.due_date)}
          </span>
        ) : (
          <span className="tboard-card__due tboard-card__due--none">No date</span>
        )}
      </div>

      {subTitle || assignee ? (
        <div className="tboard-card__meta">
          {subTitle ? <span className="tboard-card__job" title={subTitle}>{subTitle}</span> : <span />}
          {assignee ? <AssigneeAvatar assignee={assignee} /> : null}
        </div>
      ) : null}

      {row.task_type === "submit" ? (
        <span className="tboard-card__hint">Closes automatically when the application is logged.</span>
      ) : null}
    </>
  );
}
